import React, { useMemo, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Plus, Search, MoreHorizontal, Edit, Trash2, ChevronLeft, ChevronRight, ArrowUpDown, Download, Check, AlertCircle, X } from "lucide-react";
import { DataService } from "../services/supabase";
import { exportToSpreadsheet } from "../services/exportService";
import { formatDate as formatDateUtil } from "../lib/utils";

interface Expense {
  id: string;
  date: string;
  category: string;
  description?: string;
  amount: number;
  created_at: string;
  updated_at: string;
}

interface ExpensesProps {
  dataService?: DataService | null;
  onExpensesUpdate?: (expenses: Expense[]) => void;
  expenses?: Expense[];
}

const Expenses = ({ dataService, onExpensesUpdate, expenses = [] }: ExpensesProps) => {
  // State for search and filtering
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for sorting
  const [sortField, setSortField] = useState<'date' | 'category' | 'description' | 'amount'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  
  // State for exports
  const [isExporting, setIsExporting] = useState(false);
  
  // State for Add Expense dialog
  const [addExpenseDialogOpen, setAddExpenseDialogOpen] = useState(false);
  const [newExpenseData, setNewExpenseData] = useState({
    date: '',
    category: '',
    description: '',
    amount: '',
  });
  
  // State for Edit Expense dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [editData, setEditData] = useState({
    date: '',
    category: '',
    description: '',
    amount: '',
  });

  // State for delete confirmation dialog
  const [expenseToDeleteId, setExpenseToDeleteId] = useState<string | null>(null);

  // Inline success/error message (replaces browser alerts)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const clearMessage = () => setMessage(null);
  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(clearMessage, 4000);
  };
  
  // Filter, sort, and paginate expenses
  const processedExpenses = useMemo(() => {
    // Ensure expenses is an array
    const expensesArray = Array.isArray(expenses) ? expenses : [];
    
    // Filter
    let filtered = expensesArray.filter(expense => {
      const matchesSearch = 
        expense.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        expense.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        expense.amount.toString().includes(searchTerm);
      
      return matchesSearch;
    });
    
    // Sort
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (sortField) {
        case 'date':
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
          break;
        case 'category':
          aValue = a.category.toLowerCase();
          bValue = b.category.toLowerCase();
          break;
        case 'description':
          aValue = (a.description || '').toLowerCase();
          bValue = (b.description || '').toLowerCase();
          break;
        case 'amount':
          aValue = a.amount;
          bValue = b.amount;
          break;
        default:
          return 0;
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return 0;
    });
    
    return filtered;
  }, [expenses, searchTerm, sortField, sortDirection]);
  
  // Pagination calculations
  const totalPages = Math.ceil(processedExpenses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedExpenses = processedExpenses.slice(startIndex, endIndex);
  
  // Reset to page 1 when search or sorting changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortField, sortDirection]);

  // Export expenses data
  const handleExportExpenses = () => {
    if (isExporting) return;
    
    setIsExporting(true);
    try {
      const exportData = processedExpenses.map(expense => ({
        'Date': expense.date || '',
        'Category': expense.category || '',
        'Description': expense.description || '',
        'Amount': expense.amount || 0,
      }));

      if (exportData.length === 0) {
        showMessage('error', 'No expenses to export');
        setIsExporting(false);
        return;
      }

      exportToSpreadsheet(exportData, {
        filename: 'expenses',
        includeDateInFilename: true,
      });
    } catch (error) {
      console.error('Error exporting expenses:', error);
      showMessage('error', 'Failed to export expenses. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };
  
  // Helper functions
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };
  
  const handleSort = (field: 'date' | 'category' | 'description' | 'amount') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Handler for adding new expense
  const addNewExpense = async () => {
    if (!dataService) {
      console.error('Data service not available');
      return;
    }
    
    if (!newExpenseData.date || !newExpenseData.category || !newExpenseData.amount) {
      showMessage('error', 'Please fill in all required fields (Date, Category, Amount)');
      return;
    }
    
    try {
      const result = await dataService.addExpense({
        date: newExpenseData.date,
        category: newExpenseData.category,
        description: newExpenseData.description || '',
        amount: parseFloat(newExpenseData.amount),
      });
      
      // Update local state instead of refreshing
      if (onExpensesUpdate && result) {
        const allExpenses = await dataService.getExpenses();
        onExpensesUpdate(allExpenses);
      }
      
      setAddExpenseDialogOpen(false);
      setNewExpenseData({ date: '', category: '', description: '', amount: '' });
      showMessage('success', 'Expense added successfully');
    } catch (error) {
      console.error('Error adding new expense:', error);
      showMessage('error', 'Failed to add new expense');
    }
  };
  
  // Handler for opening edit dialog
  const handleEditExpense = (expense: Expense) => {
    setEditingExpense(expense);
    setEditData({
      date: expense.date,
      category: expense.category,
      description: expense.description || '',
      amount: String(expense.amount),
    });
    setEditDialogOpen(true);
  };
  
  // Handler for saving edits
  const handleSaveEdit = async () => {
    if (!dataService || !editingExpense || !editingExpense.id) {
      console.error('Data service or expense not available');
      return;
    }
    
    if (!editData.date || !editData.category || !editData.amount) {
      showMessage('error', 'Please fill in all required fields (Date, Category, Amount)');
      return;
    }
    
    try {
      await dataService.updateExpense(editingExpense.id, {
        date: editData.date,
        category: editData.category,
        description: editData.description || '',
        amount: parseFloat(editData.amount),
      });
      
      // Update local state instead of refreshing
      if (onExpensesUpdate) {
        const updatedExpenses = expenses.map(e => 
          e.id === editingExpense.id 
            ? { 
                ...e, 
                date: editData.date,
                category: editData.category,
                description: editData.description || '',
                amount: parseFloat(editData.amount),
                updated_at: new Date().toISOString()
              }
            : e
        );
        onExpensesUpdate(updatedExpenses);
      }
      
      setEditDialogOpen(false);
      setEditingExpense(null);
      showMessage('success', 'Expense updated successfully');
    } catch (error) {
      console.error('Error updating expense:', error);
      showMessage('error', 'Failed to update expense');
    }
  };
  
  // Handler for deleting an expense (call after confirmation)
  const handleDeleteExpense = async (id: string) => {
    if (!dataService) {
      console.error('Data service not available');
      return;
    }
    try {
      await dataService.deleteExpense(id);
      setExpenseToDeleteId(null);
      if (onExpensesUpdate) {
        const updatedExpenses = expenses.filter(e => e.id !== id);
        onExpensesUpdate(updatedExpenses);
      }
      showMessage('success', 'Expense deleted');
    } catch (error) {
      console.error('Error deleting expense:', error);
      showMessage('error', 'Failed to delete expense');
    }
  };
  
  // Get unique categories for dropdown
  const categories = useMemo(() => {
    const expensesArray = Array.isArray(expenses) ? expenses : [];
    const categorySet = new Set(expensesArray.map(expense => expense.category));
    const uniqueCategories = Array.from(categorySet);
    return uniqueCategories.sort();
  }, [expenses]);
  
  // Calculate totals
  const totalAmount = processedExpenses.reduce((sum, e) => sum + e.amount, 0);
  const totalExpenses = expenses.length;
  
  // Sortable Header Component
  type SortFieldType = 'date' | 'category' | 'description' | 'amount';
  const SortableHeader = ({ field, label, onSort, sortField, sortDirection }: { field: SortFieldType, label: string, onSort: (field: SortFieldType) => void, sortField: SortFieldType, sortDirection: 'asc' | 'desc' }) => (
    <button
      onClick={() => onSort(field)}
      className="flex items-center gap-2 hover:text-foreground transition-colors"
    >
      {label}
      <ArrowUpDown className={`h-4 w-4 ${sortField === field ? 'text-primary' : 'text-muted-foreground'}`} />
      {sortField === field && (
        <span className="text-xs text-muted-foreground">
          {sortDirection === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </button>
  );
  
  return (
    <div className="min-h-screen bg-background">
      <main className="container py-8 space-y-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Expenses</h1>
            <p className="text-muted-foreground mt-1">
              Track your business expenses ({totalExpenses} total, {formatCurrency(totalAmount)})
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportExpenses}
              disabled={isExporting || processedExpenses.length === 0}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Download Spreadsheet</span>
              <span className="sm:hidden">Export</span>
            </Button>
            <Button variant="gradient" size="sm" className="gap-2" onClick={() => setAddExpenseDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Add Expense</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>
        </div>

        {/* Inline success/error message */}
        {message && (
          <div className={`flex items-center gap-3 p-4 rounded-lg border ${
            message.type === 'success'
              ? 'bg-success/10 border-success/20'
              : 'bg-destructive/10 border-destructive/20'
          }`}>
            {message.type === 'success' ? (
              <div className="h-8 w-8 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0">
                <Check className="h-5 w-5 text-success" />
              </div>
            ) : (
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
            )}
            <p className={`flex-1 text-sm ${message.type === 'success' ? 'text-success' : 'text-destructive'}`}>
              {message.text}
            </p>
            <Button variant="ghost" size="sm" className="flex-shrink-0" onClick={clearMessage} aria-label="Dismiss">
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search expenses by category, description, or amount..."
            className="pl-10 glass-card"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        {/* Expenses Table */}
        <div className="space-y-4">
          <div className="glass-card rounded-xl overflow-hidden animate-fade-in-up">
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <Table className="min-w-full">
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="px-2 sm:px-4">
                      <SortableHeader field="date" label="Date" onSort={handleSort} sortField={sortField} sortDirection={sortDirection} />
                    </TableHead>
                    <TableHead className="px-2 sm:px-4">
                      <SortableHeader field="category" label="Category" onSort={handleSort} sortField={sortField} sortDirection={sortDirection} />
                    </TableHead>
                    <TableHead className="px-2 sm:px-4">
                      <SortableHeader field="description" label="Description" onSort={handleSort} sortField={sortField} sortDirection={sortDirection} />
                    </TableHead>
                    <TableHead className="px-2 sm:px-4">
                      <SortableHeader field="amount" label="Amount" onSort={handleSort} sortField={sortField} sortDirection={sortDirection} />
                    </TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedExpenses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        {expenses.length === 0 
                          ? 'No expenses found. Add some expenses to get started!'
                          : 'No expenses match your search criteria.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedExpenses.map((expense, index) => (
                      <TableRow
                        key={expense.id}
                        className="transition-colors hover:bg-muted/50 animate-fade-in"
                        style={{ animationDelay: `${index * 0.05}s` }}
                      >
                        <TableCell className="text-muted-foreground px-2 sm:px-4">
                          {formatDateUtil(expense.date, { fallback: 'N/A' })}
                        </TableCell>
                        <TableCell className="px-2 sm:px-4">
                          <Badge variant="outline" className="bg-primary/10 border-primary">
                            {expense.category}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground px-2 sm:px-4">
                          {expense.description || '-'}
                        </TableCell>
                        <TableCell className="font-medium px-2 sm:px-4">
                          {formatCurrency(expense.amount)}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem 
                                onClick={() => handleEditExpense(expense)}
                                className="flex items-center gap-2"
                              >
                                <Edit className="h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => setExpenseToDeleteId(expense.id)}
                                className="flex items-center gap-2 text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3">
              <div className="text-sm text-muted-foreground">
                Showing {startIndex + 1} to {Math.min(endIndex, processedExpenses.length)} of {processedExpenses.length} items
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
      
      {/* Add New Expense Dialog */}
      <Dialog open={addExpenseDialogOpen} onOpenChange={setAddExpenseDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Expense</DialogTitle>
            <DialogDescription>
              Add a new expense to your records. Fields marked with * are required.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="expense-date">Date *</Label>
              <Input
                id="expense-date"
                type="date"
                value={newExpenseData.date}
                onChange={(e) => setNewExpenseData(prev => ({ ...prev, date: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expense-category">Category *</Label>
              <Input
                id="expense-category"
                value={newExpenseData.category}
                onChange={(e) => setNewExpenseData(prev => ({ ...prev, category: e.target.value }))}
                placeholder="e.g., Shipping, Storage, Subscriptions"
                required
                list="categories-list"
              />
              <datalist id="categories-list">
                {categories.map(cat => <option key={cat} value={cat} />)}
              </datalist>
            </div>
            <div className="space-y-2">
              <Label htmlFor="expense-description">Description</Label>
              <Input
                id="expense-description"
                value={newExpenseData.description}
                onChange={(e) => setNewExpenseData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expense-amount">Amount *</Label>
              <Input
                id="expense-amount"
                type="number"
                step="0.01"
                value={newExpenseData.amount}
                onChange={(e) => setNewExpenseData(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0.00"
                required
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddExpenseDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="gradient" onClick={addNewExpense}>
              Add Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Expense Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Expense</DialogTitle>
            <DialogDescription>
              Edit the details of your expense. Fields marked with * are required.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-expense-date">Date *</Label>
              <Input
                id="edit-expense-date"
                type="date"
                value={editData.date}
                onChange={(e) => setEditData(prev => ({ ...prev, date: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-expense-category">Category *</Label>
              <Input
                id="edit-expense-category"
                value={editData.category}
                onChange={(e) => setEditData(prev => ({ ...prev, category: e.target.value }))}
                placeholder="e.g., Shipping, Storage, Subscriptions"
                required
                list="edit-categories-list"
              />
              <datalist id="edit-categories-list">
                {categories.map(cat => <option key={cat} value={cat} />)}
              </datalist>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-expense-description">Description</Label>
              <Input
                id="edit-expense-description"
                value={editData.description}
                onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-expense-amount">Amount *</Label>
              <Input
                id="edit-expense-amount"
                type="number"
                step="0.01"
                value={editData.amount}
                onChange={(e) => setEditData(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0.00"
                required
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="gradient" onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Expense Confirmation Dialog */}
      <Dialog open={!!expenseToDeleteId} onOpenChange={(open) => !open && setExpenseToDeleteId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Expense</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this expense? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpenseToDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => expenseToDeleteId && handleDeleteExpense(expenseToDeleteId)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Expenses;
